import _ from 'lodash';

import { wait } from 'core/helpers/browser';
import { getLoginUrl } from 'core/paths';

type TMethod = 'POST' | 'GET' | 'PUT' | 'DELETE';
type TQueryParams = { [key: string]: string | boolean | number | undefined };
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TBody = Record<string, any>;

export const API_PREFIX = localStorage.getItem('API_PREFIX') ?? '/api/';
export const LS_ACCESS_TOKEN_KEY = 'accessToken';
export const ACCESS_TOKEN = localStorage.getItem(LS_ACCESS_TOKEN_KEY);
export const ACCESS_TOKEN_STORAGE = ACCESS_TOKEN ? 'localStorage' : 'cookie';

const toQueryParams = (source: TQueryParams): string => {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(source)) {
    if (!_.isNil(value)) {
      sp.set(key, String(value));
    }
  }
  const result = sp.toString();
  return result ? `?${result}` : '';
};

class ServerError extends Error {
  isServerError = true;
  statusCode: number | null = null;

  constructor(url: string, message: string, statusCode?: number) {
    super(message);
    this.name = 'AppServerError';
    this.message = `ServerError (${url}) ${message}`;
    this.statusCode = statusCode ?? null;
  }
}

const handleSuccessResponse = async (res: Response) => {
  const result = (await res.json()) as Record<string, unknown>;

  if (res.status === 401 && location.pathname !== getLoginUrl()) {
    location.replace(getLoginUrl());
    // do not show an error
    await new Promise(resolve => setTimeout(resolve, 5_000));
  }

  if (res.status < 200 || res.status >= 300) {
    const errorMessage: string = String(
      result.message ?? 'Unknown server error'
    );
    throw new ServerError(res.url, errorMessage, res.status);
  }

  return result;
};

const handleErrorResponse = (url: string, error: unknown) => {
  if (!(error instanceof ServerError)) {
    error = new ServerError(url, `Unknown server error`);
  }
  throw error;
};

export const calcApiUrl = (
  path: string,
  queryParams: TQueryParams = {}
): string => API_PREFIX + path + toQueryParams(queryParams);

type TQueryArgs = {
  method: TMethod;
  path: string;
  queryParams?: TQueryParams;
  body?: TBody;
};
export const doRawQuery = async ({
  method,
  path,
  queryParams = {},
  body,
}: TQueryArgs) => {
  return await fetch(calcApiUrl(path, queryParams), {
    method,
    headers: {
      'Content-Type': 'application/json',
      // accessToken is stored either in http-only cookie or in local storage.
      // @see env.JWT_STORAGE
      accessToken: ACCESS_TOKEN ?? undefined,
    } as HeadersInit,
    mode: 'cors',
    body: body ? JSON.stringify(body) : undefined,
  });
};

const query = async ({
  method,
  path,
  queryParams,
  body,
}: TQueryArgs): Promise<unknown> => {
  try {
    const res = await doRawQuery({ method, path, queryParams, body });
    return await handleSuccessResponse(res);
  } catch (error) {
    handleErrorResponse(calcApiUrl(path, queryParams), error);
  }
};

// Put all queries in a set to be able to wait until any possible API query is
// finished. May be useful to avoid races.
const doQuery: typeof query = (...args) => {
  const promise = query(...args);
  pendingQueries.add(promise);
  void promise.finally(() => pendingQueries.delete(promise));
  return promise;
};

export const doGetQuery = <TResult,>(
  path: string,
  queryParams: TQueryParams = {}
): Promise<TResult> =>
  doQuery({
    method: 'GET',
    path,
    queryParams,
  }) as Promise<TResult>;

export const doPostQuery = <TResult,>(
  path: string,
  queryParams?: TQueryParams | null,
  body?: TBody
): Promise<TResult> =>
  doQuery({
    method: 'POST',
    path,
    queryParams: queryParams || {},
    body,
  }) as Promise<TResult>;

export const doPutQuery = <TResult,>(
  path: string,
  queryParams?: TQueryParams | null,
  body?: TBody
): Promise<TResult> =>
  doQuery({
    method: 'PUT',
    path,
    queryParams: queryParams || {},
    body,
  }) as Promise<TResult>;

export const doDeleteQuery = <TResult,>(
  path: string,
  queryParams?: TQueryParams | null,
  body?: TBody
): Promise<TResult> =>
  doQuery({
    method: 'DELETE',
    path,
    queryParams: queryParams || {},
    body,
  }) as Promise<TResult>;

const pendingQueries = new Set<Promise<unknown>>();
export const waitForPendingQueries = async (): Promise<unknown> => {
  // nothing to wait.
  if (!pendingQueries.size) return;

  await Promise.race([
    new Promise<void>(resolve => setTimeout(resolve, 3_000)),
    Promise.all(pendingQueries.values())
      // Give some time to update the database.
      .then(() => wait(300)),
  ]);

  // repeat, it may be a series of queries.
  return await waitForPendingQueries();
};

export const apiLog = console.log;
